import React, { useEffect, useMemo, useState } from "react";
import { courseList, courseDelete } from "../../../api/Courses";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";

import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";

import { SweetAlertShow, LoadingDiv } from "../../master/Master";

import usePrivateLink from "../../config/usePrivateLink";
import AcademicPortalLayout from "../AcademicPortalLayout";

export default function Courses() {
  const hasAccess = usePrivateLink();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  /* =========================================================
     FETCH COURSES
  ========================================================= */

  const courseFetch = async () => {
    setLoading(true);

    try {
      const response = await courseList();

      if (response?.data?.status) {
        setCourses(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     DELETE COURSE
  ========================================================= */

  const handleCourseDelete = async (course) => {
    const result = await Swal.fire({
      title: "Delete Course?",
      html: `
        <div style="font-size:13px; line-height:1.6;">
          You are about to delete
          <strong>${course?.title || "this course"}</strong>.
          <br />
          This action cannot be undone.
        </div>
      `,
      icon: "warning",

      showCancelButton: true,

      confirmButtonText: '<i class="ri-delete-bin-line me-1"></i> Delete',

      cancelButtonText: '<i class="ri-close-line me-1"></i> Cancel',

      buttonsStyling: true,

      customClass: {
        popup: "course-delete-popup",
        confirmButton: "btn btn-danger",
        cancelButton: "btn btn-secondary ms-2",
      },
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);

    try {
      const response = await courseDelete(course?.id);

      if (response?.data?.status) {
        await courseFetch();

        SweetAlertShow("success", "Deleted", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     SEARCH
  ========================================================= */

  const filteredCourses = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    if (!searchValue) {
      return courses;
    }

    return courses.filter((course) => {
      const courseName = course?.title?.toLowerCase() || "";

      const subjects =
        course?.subjects_list
          ?.map((subject) => subject?.name?.toLowerCase())
          .join(" ") || "";

      return courseName.includes(searchValue) || subjects.includes(searchValue);
    });
  }, [courses, search]);

  /* =========================================================
     STATS
  ========================================================= */

  const totalSubjects = useMemo(() => {
    return courses.reduce(
      (total, course) => total + (course?.subjects_list?.length || 0),
      0,
    );
  }, [courses]);

  useEffect(() => {
    courseFetch();
  }, []);

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <AcademicPortalLayout />

                  <div className="col-12">
                    {/* =================================================
                        MAIN CARD
                    ================================================= */}

                    <div className="card courses-page-card">
                      {/* =============================================
                          HEADER
                      ============================================= */}

                      <div className="courses-header">
                        <div className="d-flex align-items-start gap-3">
                          <div className="courses-header-icon">
                            <i className="ri-book-open-line"></i>
                          </div>

                          <div>
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <h4 className="mb-0 fw-semibold">
                                Courses / Classes
                              </h4>

                              <span className="badge bg-label-primary">
                                {courses.length}
                              </span>
                            </div>

                            <p className="text-muted mb-0 mt-1">
                              Manage academic classes and their subjects
                            </p>
                          </div>
                        </div>

                        {/* Add Button */}

                        {hasAccess("/course/add") && (
                          <Link to="/course/add" className="btn btn-primary">
                            <i className="ri-add-line me-1"></i>
                            Add Course / Class
                          </Link>
                        )}
                      </div>

                      {/* =============================================
                          SUMMARY
                      ============================================= */}

                      <div className="courses-summary">
                        <div className="course-summary-item">
                          <div className="course-summary-icon">
                            <i className="ri-book-2-line"></i>
                          </div>

                          <div>
                            <span>Total Courses</span>

                            <strong>{courses.length}</strong>
                          </div>
                        </div>

                        <div className="course-summary-item">
                          <div className="course-summary-icon">
                            <i className="ri-bookmark-3-line"></i>
                          </div>

                          <div>
                            <span>Total Subjects</span>

                            <strong>{totalSubjects}</strong>
                          </div>
                        </div>

                        <div className="course-summary-item">
                          <div className="course-summary-icon">
                            <i className="ri-search-line"></i>
                          </div>

                          <div>
                            <span>Showing</span>

                            <strong>{filteredCourses.length}</strong>
                          </div>
                        </div>
                      </div>

                      {/* =============================================
                          TOOLBAR
                      ============================================= */}

                      <div className="courses-toolbar">
                        <div>
                          <h6 className="mb-0 fw-semibold">Course List</h6>

                          <small className="text-muted">
                            Select a course to edit or manage its subjects
                          </small>
                        </div>

                        <div className="course-search">
                          <i className="ri-search-line"></i>

                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search course or subject..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                          />

                          {search && (
                            <button
                              type="button"
                              className="course-search-clear"
                              onClick={() => setSearch("")}
                            >
                              <i className="ri-close-line"></i>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* =============================================
                          TABLE
                      ============================================= */}

                      <div className="table-responsive">
                        <table className="table table-hover align-middle mb-0 courses-table">
                          <thead>
                            <tr>
                              <th className="text-center">#</th>

                              <th>Course / Class</th>

                              <th>Subjects</th>

                              <th className="text-center">Subject Count</th>

                              <th className="text-center">Action</th>
                            </tr>
                          </thead>

                          <tbody>
                            {filteredCourses.length === 0 ? (
                              <tr>
                                <td colSpan="5" className="py-5">
                                  <div className="course-empty-state">
                                    <div className="course-empty-icon">
                                      <i className="ri-book-search-line"></i>
                                    </div>

                                    <h6 className="mb-1">
                                      {search
                                        ? "No matching courses"
                                        : "No courses available"}
                                    </h6>

                                    <p className="text-muted mb-3">
                                      {search
                                        ? "Try searching with another course or subject name."
                                        : "Create your first course or class to get started."}
                                    </p>

                                    {search ? (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={() => setSearch("")}
                                      >
                                        <i className="ri-close-line me-1"></i>
                                        Clear Search
                                      </button>
                                    ) : (
                                      hasAccess("/course/add") && (
                                        <Link
                                          to="/course/add"
                                          className="btn btn-sm btn-primary"
                                        >
                                          <i className="ri-add-line me-1"></i>
                                          Add Course
                                        </Link>
                                      )
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ) : (
                              filteredCourses.map((item, index) => {
                                const subjects = item?.subjects_list || [];

                                return (
                                  <tr key={item.id}>
                                    {/* Number */}

                                    <td className="text-center">
                                      <span className="course-index">
                                        {index + 1}
                                      </span>
                                    </td>

                                    {/* Course */}

                                    <td>
                                      <div className="course-name-cell">
                                        <div className="course-avatar">
                                          {(item?.title || "C")
                                            .charAt(0)
                                            .toUpperCase()}
                                        </div>

                                        <div>
                                          <strong>
                                            {item?.title || "N/A"}
                                          </strong>

                                          <small>Course / Class</small>
                                        </div>
                                      </div>
                                    </td>

                                    {/* Subjects */}

                                    <td>
                                      {subjects.length > 0 ? (
                                        <div className="subject-list">
                                          {subjects.map((subject, i) => (
                                            <span
                                              key={subject?.id ?? i}
                                              className="subject-chip"
                                            >
                                              {subject?.name || "N/A"}
                                            </span>
                                          ))}
                                        </div>
                                      ) : (
                                        <span className="text-muted small">
                                          No subjects
                                        </span>
                                      )}
                                    </td>

                                    {/* Count */}

                                    <td className="text-center">
                                      <span className="subject-count">
                                        {subjects.length}
                                      </span>
                                    </td>

                                    {/* Actions */}

                                    <td>
                                      <div className="course-actions">
                                        {hasAccess("/course/update") && (
                                          <Link
                                            className="btn btn-sm btn-icon btn-outline-primary"
                                            to={`/course/update?id=${item?.id}`}
                                            title="Edit Course"
                                          >
                                            <i className="ri-pencil-line"></i>
                                          </Link>
                                        )}

                                        {hasAccess("/course/delete") && (
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-icon btn-outline-danger"
                                            onClick={() =>
                                              handleCourseDelete(item)
                                            }
                                            title="Delete Course"
                                          >
                                            <i className="ri-delete-bin-line"></i>
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* =============================================
                          FOOTER
                      ============================================= */}

                      {filteredCourses.length > 0 && (
                        <div className="courses-footer">
                          <span className="text-muted">
                            Showing <strong>{filteredCourses.length}</strong> of{" "}
                            <strong>{courses.length}</strong> courses
                          </span>

                          {search && (
                            <span className="text-muted">
                              Search results for <strong>"{search}"</strong>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          STYLES
      ========================================================= */}

      <style>{`

        /* =====================================================
           MAIN CARD
        ===================================================== */

        .courses-page-card {
          overflow: hidden;

          border: 1px solid
            rgba(127, 127, 127, 0.14);
        }


        /* =====================================================
           HEADER
        ===================================================== */

        .courses-header {
          padding: 20px 22px;

          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 15px;
          flex-wrap: wrap;

          border-bottom: 1px solid
            rgba(127, 127, 127, 0.14);
        }

        .courses-header-icon {
          width: 46px;
          height: 46px;
          min-width: 46px;

          border-radius: 11px;

          display: flex;
          align-items: center;
          justify-content: center;

          background: rgba(
            var(--bs-primary-rgb),
            0.10
          );

          color: var(--bs-primary);

          font-size: 23px;
        }

        .courses-header h4 {
          font-size: 19px;
        }

        .courses-header p {
          font-size: 12px;
        }


        /* =====================================================
           SUMMARY
        ===================================================== */

        .courses-summary {
          padding: 14px 22px;

          display: grid;
          grid-template-columns:
            repeat(3, minmax(0, 1fr));

          gap: 10px;

          border-bottom: 1px solid
            rgba(127, 127, 127, 0.12);
        }

        .course-summary-item {
          min-height: 66px;

          padding: 10px 12px;

          display: flex;
          align-items: center;

          gap: 10px;

          border: 1px solid
            rgba(127, 127, 127, 0.14);

          border-radius: 9px;
        }

        .course-summary-icon {
          width: 34px;
          height: 34px;
          min-width: 34px;

          display: flex;
          align-items: center;
          justify-content: center;

          border-radius: 8px;

          background: rgba(
            var(--bs-primary-rgb),
            0.08
          );

          color: var(--bs-primary);

          font-size: 17px;
        }

        .course-summary-item span {
          display: block;

          font-size: 10px;

          opacity: 0.60;

          margin-bottom: 2px;
        }

        .course-summary-item strong {
          display: block;

          font-size: 16px;

          font-weight: 700;
        }


        /* =====================================================
           TOOLBAR
        ===================================================== */

        .courses-toolbar {
          padding: 15px 22px;

          display: flex;
          align-items: center;
          justify-content: space-between;

          gap: 15px;
          flex-wrap: wrap;

          border-bottom: 1px solid
            rgba(127, 127, 127, 0.12);
        }

        .courses-toolbar h6 {
          font-size: 14px;
        }

        .courses-toolbar small {
          font-size: 11px;
        }


        /* =====================================================
           SEARCH
        ===================================================== */

        .course-search {
          position: relative;

          width: 300px;
        }

        .course-search > i {
          position: absolute;

          left: 12px;
          top: 50%;

          transform: translateY(-50%);

          font-size: 17px;

          opacity: 0.55;

          z-index: 2;

          pointer-events: none;
        }

        .course-search .form-control {
          height: 39px;

          padding-left: 36px;
          padding-right: 36px;

          border-radius: 8px;

          font-size: 12px;
        }

        .course-search-clear {
          position: absolute;

          right: 7px;
          top: 50%;

          transform: translateY(-50%);

          width: 27px;
          height: 27px;

          border: 0;

          border-radius: 6px;

          background: transparent;

          color: inherit;

          display: flex;
          align-items: center;
          justify-content: center;

          cursor: pointer;

          opacity: 0.55;
        }

        .course-search-clear:hover {
          opacity: 1;

          background: rgba(
            127,
            127,
            127,
            0.08
          );
        }


        /* =====================================================
           TABLE
        ===================================================== */

        .courses-table {
          min-width: 850px;
        }

        .courses-table thead th {
          padding: 12px 15px;

          font-size: 11px;

          text-transform: uppercase;

          letter-spacing: 0.25px;

          white-space: nowrap;

          font-weight: 700;
        }

        .courses-table tbody td {
          padding: 13px 15px;

          font-size: 13px;
        }

        .courses-table tbody tr {
          transition:
            background 0.15s ease;
        }


        /* =====================================================
           INDEX
        ===================================================== */

        .course-index {
          width: 28px;
          height: 28px;

          display: inline-flex;

          align-items: center;
          justify-content: center;

          border-radius: 7px;

          background: rgba(
            127,
            127,
            127,
            0.08
          );

          font-size: 11px;
          font-weight: 600;
        }


        /* =====================================================
           COURSE NAME
        ===================================================== */

        .course-name-cell {
          display: flex;

          align-items: center;

          gap: 10px;

          min-width: 190px;
        }

        .course-avatar {
          width: 38px;
          height: 38px;

          min-width: 38px;

          border-radius: 9px;

          display: flex;
          align-items: center;
          justify-content: center;

          background: rgba(
            var(--bs-primary-rgb),
            0.10
          );

          color: var(--bs-primary);

          font-size: 14px;

          font-weight: 700;
        }

        .course-name-cell strong {
          display: block;

          font-size: 13px;

          font-weight: 600;
        }

        .course-name-cell small {
          display: block;

          font-size: 10px;

          opacity: 0.55;

          margin-top: 2px;
        }


        /* =====================================================
           SUBJECTS
        ===================================================== */

        .subject-list {
          display: flex;

          align-items: center;

          flex-wrap: wrap;

          gap: 5px;

          max-width: 600px;
        }

        .subject-chip {
          display: inline-flex;

          align-items: center;

          padding: 5px 8px;

          border-radius: 6px;

          background: rgba(
            127,
            127,
            127,
            0.08
          );

          border: 1px solid
            rgba(127, 127, 127, 0.10);

          font-size: 11px;

          line-height: 1.2;
        }


        /* =====================================================
           SUBJECT COUNT
        ===================================================== */

        .subject-count {
          min-width: 30px;
          height: 28px;

          padding: 0 8px;

          display: inline-flex;

          align-items: center;
          justify-content: center;

          border-radius: 7px;

          background: rgba(
            var(--bs-primary-rgb),
            0.08
          );

          color: var(--bs-primary);

          font-size: 12px;

          font-weight: 700;
        }


        /* =====================================================
           ACTIONS
        ===================================================== */

        .course-actions {
          display: flex;

          justify-content: center;

          gap: 6px;
        }

        .btn-icon {
          width: 32px;
          height: 32px;

          padding: 0;

          display: inline-flex;

          align-items: center;
          justify-content: center;

          border-radius: 7px;
        }

        .btn-icon i {
          font-size: 15px;
        }


        /* =====================================================
           EMPTY STATE
        ===================================================== */

        .course-empty-state {
          display: flex;

          flex-direction: column;

          align-items: center;

          justify-content: center;

          text-align: center;
        }

        .course-empty-icon {
          width: 56px;
          height: 56px;

          margin-bottom: 12px;

          display: flex;

          align-items: center;
          justify-content: center;

          border-radius: 13px;

          background: rgba(
            var(--bs-primary-rgb),
            0.08
          );

          color: var(--bs-primary);

          font-size: 26px;
        }

        .course-empty-state h6 {
          font-size: 14px;

          font-weight: 600;
        }

        .course-empty-state p {
          max-width: 380px;

          font-size: 12px;
        }


        /* =====================================================
           FOOTER
        ===================================================== */

        .courses-footer {
          padding: 12px 22px;

          display: flex;

          align-items: center;
          justify-content: space-between;

          gap: 10px;

          flex-wrap: wrap;

          border-top: 1px solid
            rgba(127, 127, 127, 0.12);

          font-size: 11px;
        }


        /* =====================================================
           RESPONSIVE
        ===================================================== */

        @media (max-width: 767px) {

          .courses-summary {
            grid-template-columns: 1fr;
          }

          .courses-header,
          .courses-toolbar {
            align-items: stretch;
          }

          .courses-header > a {
            width: 100%;
          }

          .course-search {
            width: 100%;
          }

        }


        @media (max-width: 575px) {

          .courses-header,
          .courses-toolbar,
          .courses-footer {
            padding-left: 15px;
            padding-right: 15px;
          }

          .courses-summary {
            padding-left: 15px;
            padding-right: 15px;
          }

          .courses-header-icon {
            width: 42px;
            height: 42px;
            min-width: 42px;
          }

          .courses-header h4 {
            font-size: 17px;
          }

        }

      `}</style>
    </>
  );
}
